import Field, { IField } from './field'
import DataTransformer from './filter'
import {
  getRelativePath,
  walk,
  getType,
  hasParent,
  getParent,
  types as t,
  getSnapshot,
  getRoot,
  getEnv,
  hasParentOfType,
  getParentOfType,
  resolvePath,
  IAnyModelType,
  Instance,
  SnapshotIn
} from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import Component, {
  Component as ComponentItem
} from './component'
import DataSource from './datasource'
import isArray from 'lodash/isArray'
import each from 'lodash/each'
import ref, { safeReference } from './reference'
import Mapper from './props-mapper'
import { getRecordClass } from './record/record'
import typeCheck from '../../utils/typeCheck'
import { dataSourcesFieldSets, getAllDataSources } from '../../utils/deptree'
import ssrProps from '../../utils/ssr-props'
import type { FunctionLabel, LabelEntry, MappingType, MappingValue } from './types'
import get from 'lodash/get'

const Primitive = t.union(
  {},
  t.string,
  t.boolean,
  t.number,
  t.literal<null>(null),
  t.literal<undefined>(undefined)
)

const FieldSet = t.array(Field)

const Prop = t.union(
  {
    dispatcher: snapshot => {
      if (snapshot) {
        if (Object.keys(snapshot).length > 1 && (snapshot.fieldPath || (snapshot.label && snapshot.type))) { return Field }
        if (
          Array.isArray(snapshot) &&
          snapshot.length > 0 &&
          snapshot[0] &&
          ((snapshot[0].label && !snapshot[0].value) || snapshot[0].fieldPath)
        ) {
          return FieldSet
        }
        if (typeof snapshot === 'object') {
          if (snapshot.table) return t.late(() => DataTransformer)
          return t.frozen()
        }
      }
      return Primitive
    }
  },
  Primitive,
  FieldSet,
  Field,
  t.late(() => DataTransformer),
  t.array(t.number),
  t.frozen()
)

const Mapping = t
.model('Mapping', {
  children: t.maybeNull(t.array(t.late((): IAnyModelType => Mapping))),
  key: t.maybeNull(t.union(t.string, t.number)),
  type: t.maybeNull(t.string),
  dataSource: ref(t.late((): IAnyModelType => DataSource as any), 'dataSources'),
  componentPath: t.maybeNull(t.string),
  mappingPath: t.maybeNull(t.string),
  bindingType: t.maybeNull(t.string),
  js: t.maybeNull(t.boolean),
  intervalMs: t.maybeNull(t.number),
  value: Prop,
  recordType: t.maybeNull(t.union(t.string, FieldSet)),
  meta: t.frozen()
})
  .views(self => ({
    get parentComponent (): IMapping {
      let currentNode = self as any
      if (currentNode.comp) return currentNode.comp
      while (currentNode != null) {
        currentNode = hasParent(currentNode) ? getParent(currentNode) : null
        if (currentNode?.comp) {
          return currentNode.comp
        }
      }
      return null
    },
    get isBinding () {
      return (
        self.bindingType &&
        self.bindingType !== 'null' &&
        self.bindingType !== 'value'
      )
    }
  }))
  .actions(crudActions)
  .actions(self => ({
    newMapper () {
      if (!(self as any)._mapper) (self as any)._mapper = new Mapper(self as IMapping)
    },
    setComponentMapping (comp) {
      try {
        self.componentPath = getRelativePath((self as any).parentComponent, comp)
        if (self.componentPath === '') self.componentPath = '.'
      } catch (e) {
        self.componentPath = null
      }
    },
    setMappedToMapping (mapping) {
      try {
        self.mappingPath = getRelativePath(self, mapping)
        if (self.mappingPath === '') self.mappingPath = null
      } catch (e) {
        self.mappingPath = null
      }
    },
    findParent (levels = 2) : IMapping {
      return hasParent(self, levels) ? getParent(self, levels) : null
    },
  }))
  .views(self => ({
    get fullKey () {
      let parent = self
      let key = []
      while (parent) {
        if (getType(parent) !== Mapping) break
        key.push(parent.key)
        parent = (parent as any).parent
      }
      return key.reverse().join('.')
    },
    set componentMapping (comp) {
      self.setComponentMapping(comp)
    },
    get componentMapping () {
      if (!self.componentPath) return
      let node
      try {
        node = resolvePath(self.parentComponent, self.componentPath)
      } catch (e) {
        return
      }
      if(node?.id) return node
    },
    get isBoundToRef () {
      if (self.isBinding && self.value && /^\$/.test((self.value as IField).fieldPath)) {
        return true
      }
      return false
    },
    set mappedToMapping (comp) {
      self.setMappedToMapping(comp)
    },
    get mappedToMapping () {
      if (!self.mappingPath) return
      let node
      try {
        node = resolvePath(self, self.mappingPath)
      } catch (e) {
        return
      }
      return node
    },
    get mapper (): Mapper {
      if (!(self as any)._mapper) self.newMapper()
      return (self as any)._mapper
    },
    get toProps () {
      return ((self as any).mapper as Mapper)?.toProps
    },
    getCompleteFieldSet (dsfs = {}) {
      return dataSourcesFieldSets(self, dsfs)
    }    
  }), { componentMapping: { memoize: true }})
  .views(self => ({
    get hasInterval () : boolean {
      if(self.intervalMs) return true
      return self.children?.some(c => c.hasInterval)
    },
    get parent (): IMapping {
      const p = self.findParent()
      if (p && (getType(p) === Mapping || getType(p) === MappingItem)) return p
    },
    get defaultMapping () : IMapping {
      if (this.parent?.defaultMapping?.children) {
        return this.parent.defaultMapping.children.find(c => c.key === self.key)
      }

      return self.parentComponent?.defaultMapping
    },
    get info () : any {
      return this.defaultMapping?.info
    },
    get description () {
      return this.info?.description
    },
    get picklist () {
      return self.meta?.picklist || this.info?.picklist || []
    },
    set picklist (v) {
      self.update({ meta: {...self.meta || {}, picklist: v }})
    },
    get siblingDataSource () : any {
      const parentArr = getParent(self) as {dataSource: any}[]
      let dataSource: typeof DataSource
      if (parentArr && parentArr.length) {
        const mapping = parentArr.find(m => m.dataSource)
        dataSource = mapping && mapping.dataSource
      }
      return dataSource
    },
    get lastChildNum () : number {
      if (!self.children?.length) return 0
      let n = self.children.length - 1
      for (const c of self.children) {
        if (parseInt(c.key as string) > n) n = parseInt(c.key as string)
      }
      return n + 1
    },
    get acceptsChildren () : boolean {
      return (
        self.children && self.children.find(c => c.key === 'children') != null
      )
    },
    get recordFields () {
      const idOrFields = self.recordType || ((self as any).dataSourceId as string)
      if (!idOrFields) return
      
      let findFn = (getRoot(self) as any)?.getOrLoadObject || (getEnv(self) as any)?.getOrLoadObject
      if (typeof idOrFields === 'string') {
        const ds = findFn('dataSources', idOrFields)
        return ds && (ds.fields as Instance<typeof Field>[])
      }
      return idOrFields
    },
    get fieldSet () {
      return self.getCompleteFieldSet()
    },
    getAllDataSources (all = new Set()) {
      return getAllDataSources(self, all)
    },
    getAllMappingsBoundToComponent(comp, all = []) {
      if(self.componentMapping && (self.componentMapping === comp || !comp)) all.push(self)
      for(let c of self.children || []) {
        c.getAllMappingsBoundToComponent(comp, all)
      }
    },
    get dataSourceId () : string {
      return self.dataSource && self.dataSource.id
    },
    set dataSourceId (id) {
      self.dataSource = id
    },
    get canHaveFilter () : boolean {
      return self.dataSource && self.type === 'list'
    },
    get isParentMulti () : boolean {
      return this.parent && this.parent.bindingType === 'multi'
    },
    get rootMapping () : IMapping {
      let currentNode = self
      let lastMapping = self
      while (currentNode != null) {
        if (getType(currentNode) === MappingItem || getType(currentNode) === Mapping) {
          lastMapping = currentNode
        }
        currentNode = hasParent(currentNode) ? getParent(currentNode) : null
      }
      return lastMapping as IMapping
    },
    findChild (comparator: Object|(<T>(ary: T[], item: T) => boolean)) {
      if (!comparator) return false
      let child: IAnyModelType = null
      switch (typeof comparator) {
        case 'function':
          walk(self, item => {
            if (!child && comparator(self as any, item)) {
              child = item
            }
          })
          break
        case 'object':
          walk(self, item => {
            let itemMatches = true
            each(comparator, (value, key) => {
              if (item[key] !== value) itemMatches = false
            })
            if (!child && itemMatches) {
              child = item
            }
          })
          break
        default:
          return false
      }
      return child === null ? false : child
    },
    get ItemClass () {
      return getRecordClass(self)
    },
    getDSProvider (factory) {
      return factory ? factory.getInstance(this.dataSourceId) : null
    },
    get parentDataSource () : any {
      if (getEnv(self)?.parentDataSource) return getEnv(self).parentDataSource
      if (hasParentOfType(self, DataSource)) return getParentOfType(self, DataSource)
    },
    get fieldPath () : string | number {
      const parent = self.findParent()
      if (parent && getType(parent) === Mapping) {
        return (
          (parent.fieldPath && parent.fieldPath + '.' + self.key) || self.key
        )
      }
    },

    get multiLabels () : any {
      if (self.bindingType === 'multi') {
        return self.children.map((m, i) => ({
          key: m.key || i + '',
          label: m.key || i + '',
          secondaryLabel: m.type,
          meta: { mapping: m, component: m.parentComponent }
        }))
      }
    },
    get parentMultiLabels () {
      let parent = this.parent
      let multiLabels = []
      while (parent) {
        if (parent.multiLabels) { multiLabels = [...multiLabels, ...parent.multiLabels] }
        parent = parent.parent
      }
      return multiLabels
    },
    get asObject () : { [key: string]: IMapping } {
      if (!self || !self.children) return null
      if (Array.isArray(self.children) && self.children.length < 1) return null
      const childrenByKeys = {}
      self.children.forEach(currChild => {
        childrenByKeys[currChild.key] = currChild
      })

      return childrenByKeys
    },

    labels (comp = {}): LabelEntry {
      if (self.componentMapping === comp) return null
      if (self.key === 'children') return null
      const childNodes = self.children
        ? self.children.map(c => c.labels(comp))
        : []

      if ((self as any).dataTransformer) childNodes.push((self as any).dataTransformer.labels())
      return {
        key: self.key + '',
        label: self.key + '',
        secondaryLabel: self.type,
        meta: { component: self.parentComponent, mapping: self },
        childNodes: childNodes
      }
    },
    get ssrProps() {
      const parentProps = self.parent?.ssrProps
      if(parentProps) return get(parentProps, self.key)
    }
  }), { ItemClass: { memoize: true }})
  .actions(self => ({
    clear () : void {
      const parent = hasParent(self, 2) ? getParent<IMapping>(self, 2) : null
      if (
        parent &&
        parent.bindingType === 'value' &&
        (parent.type === 'list' || parent.type === 'FieldSet') &&
        parent.children
      ) {
        self.del()
      } else {
        self.dataSource = null
        self.componentPath = null
        self.mappingPath = null
        self.bindingType = null
        self.children = null
        self.mapper.setError(null)
        if (self.type !== 'slider') self.value = null
      }
    },
    addChild (map = {}) : IMapping {
      const isObject = self.type === 'object' || self.type.toLowerCase() === 'record'
      const isArray = self.type === 'list' || self.type === 'FieldSet'
      
      let defaultMap: IMappingSnapshot = {
        key: 'p' + self.lastChildNum,
        type: 'string'
      }
      
      if(isArray) {	  
	const prev = self.children?.[self.children.length - 1]
	if(prev) {
	  function clone(child: IMappingSnapshot, obj: IMappingSnapshot = { children: [] }) {
	    for(let c of child.children || []) {
	      obj.children.push(clone(c))
	    }
	    obj.type = child.type
	    obj.key = child.key
	    return obj
	  }

	  defaultMap = clone(prev)
	  defaultMap.key = self.children.length
	}
      }
      const mapping = { ...defaultMap, ...map }
      if (!self.children) (self.children as Array<SnapshotIn<typeof Mapping>>) = [mapping]
      else (self.children as Array<SnapshotIn<typeof Mapping>>).push(mapping)
      return self.children[self.children.length - 1]
    }
  }))
  .actions(self => ({
    updateChild (_key, _value?: any) {
      // Allow two types of inputs for _key. It can follow first path of key value pair,
      //   defaulting to value binding type and guessing the type of the value.
      if (typeCheck(_key) === 'string') {
        let childExists = self.children && self.children.find(item => { return item.key === _key })
        const valType = typeCheck(_value)
        if (!childExists) {
          childExists = self.addChild({ key: _key, type: valType, bindingType: 'value' })
        }
        return childExists.update({ value: _value, bindingType: childExists.bindingType || 'value' })
      } else {
        const fullChild = _key
        let childExists = self.children && self.children.find(item => { return item.key === fullChild.key })
        if (!childExists) {
          childExists = self.addChild(fullChild)
        } else {
          childExists = childExists.update(fullChild)
        }
        return childExists
      }
    }
  }))

interface IFieldTypeType { name: MappingType, raw?: string, defaultValue?: { value: unknown }, value?: unknown }
interface FieldType {
  name?: string
  description?: string
  props?: Record<string, unknown>
  type?: IFieldTypeType | MappingType
  value?: unknown
  defaultValue?: { value: unknown }
}

export const forType = (
  { description, props, type, value, name, defaultValue }: FieldType = ({} as any),
  key?: string | number,
  parent?: { type: string },
  ignoreDefaults = false
) => {
  const defaultProps = !ignoreDefaults && defaultValue && defaultValue.value;
  let typeName: string
  let childVal
  if (!type && name) type = { name: name as MappingType }

  const fieldtypeType = type as IFieldTypeType;
  if ((type as MappingType) === 'hidden') {
    typeName = 'hidden'
  } else if (!fieldtypeType || !fieldtypeType.name || fieldtypeType.name.indexOf('shape') === 0) {
    typeName = 'object'
  } else if (fieldtypeType && fieldtypeType.name.indexOf('array') === 0) {
    typeName = 'list'
  } else {
    typeName = fieldtypeType?.name || name || 'string'
  }
  const info = { description }
  const mapping: Partial<IMapping> = { key, type: typeName as MappingType, info, value: undefined }
  if (fieldtypeType && fieldtypeType.name === 'custom') {
    // handle the custom mapping types we have created...
    switch (fieldtypeType.raw) {
      case 'color':
        mapping.type = 'color'
        if (!ignoreDefaults && fieldtypeType?.defaultValue) {
          mapping.bindingType = 'value'
          mapping.value = fieldtypeType.defaultValue.value as string
        }
        break
      case 'slider':
        mapping.type = 'slider'
        mapping.bindingType = 'value'
        // if PropTypes dont match defaultProps, dont break the app.
        mapping.value = [0, 1, 1, 0]
        if (defaultProps && typeof defaultProps === 'object') {
          let vals: number[] = Object.values(defaultProps as number[])
          if (vals.length == 4) {
            mapping.value = vals
          }
        }
        break
      case 'multiselect':
        mapping.type = 'multiselect'
        mapping.info.picklist = []
        if (Array.isArray(defaultProps) && defaultProps.length && defaultProps[0] !== null) {
          mapping.info.picklist = defaultProps
        }
        break
      case 'css':
        if (fieldtypeType?.defaultValue) {
          mapping.type = 'css'
          mapping.value = fieldtypeType.defaultValue.value as string
        }
        break
      default:
        if (fieldtypeType?.raw) mapping.type = fieldtypeType.raw
    }
  } else {
    const obj = value || props || fieldtypeType?.value
    switch (typeName) {
      case 'object':
        if (obj) {
          // if some obj value is available...
          (mapping.children as Array<IMappingSnapshot>) = Object.keys(obj).map(p => {
            childVal = obj[p]
            if (defaultProps?.[p] && typeof childVal === 'object') {
              if (props) {
                if (!childVal.defaultValue) childVal.defaultValue = {}
                childVal.defaultValue.value = defaultProps[p]
              } else {
                childVal.type = obj[p]
                if (!childVal.defaultValue) childVal.defaultValue = {}
                childVal.defaultValue.value = defaultProps[p]
              }
            } else {
              childVal = props ? obj[p] : { type: obj[p] }
            }
            return forType(childVal, p, { type: mapping?.type }, ignoreDefaults)
          })
        } else if (defaultProps && typeof defaultProps === 'object' && parent?.type === 'object') {
          // if default props AND
          // the parent container is an object then make default Prop children it's direct descendants
          const childChildren = createObjectMappingChildren(defaultProps, mapping);
          (mapping.children as Array<IMappingSnapshot>) = childChildren
          mapping.bindingType = 'value'
        } else if (defaultProps && typeof defaultProps === 'object') {
          // else if there is defaultProps
          // and the parent container is a list
          // We need to make a shell object mapping to store everything in...
          // then set all of the key/val pairs to the shell's props
          const childObj = forType({ type: 'object' }, 0, { type: mapping?.type }, ignoreDefaults);
          (mapping.children as Array<IMappingSnapshot>) = [childObj]
          const childChildren = createObjectMappingChildren(
            defaultProps,
            childObj
          )
          childObj.update('children', childChildren)
          mapping.bindingType = 'value'
        }
        break
      case 'list':
        const typeValue: { name: string, value: unknown } = fieldtypeType?.value as any
        if (typeValue?.name === 'instanceOf') {
          mapping.type = typeValue.value as MappingType
        } else {
          if (Array.isArray(defaultProps) && defaultProps.length) {
            (mapping.children as Array<IMappingSnapshot>) = defaultProps.map((val, idx) => {
              if (
                typeValue.name === 'object' &&
                typeof val === 'object'
                // if one of the children is an object
                // create an object mapping container
                // then shove each key/val pair into it as children
              ) {
                const childObjContainer = forType(
                  { type: 'object' },
                  idx,
                  { type: mapping?.type },
                  ignoreDefaults
                )

                const childVals = Object.keys(val).map(k => {
                  childVal = {}
                  childVal.name = isArray(val[k]) ? 'list' : typeof val[k]
                  return forType(
                    { ...childVal, defaultValue: { value: val[k] } },
                    k,
                    { type: childObjContainer?.type },
                    ignoreDefaults
                  )
                });
                (childObjContainer.children as Array<IMappingSnapshot>) = childVals
                return childObjContainer
              } else {
                // if it's not an object, recursively make a mapping for it
                return forType(
                  { ...typeValue, defaultValue: { value: val } },
                  idx,
                  { type: mapping?.type },
                  ignoreDefaults
                )
              }
            })
            mapping.bindingType = 'value'
          } else {
            // if theres no defaultProps, just make children an array of one thing with the key of 0
            (mapping.children as Array<IMappingSnapshot>) = [forType(fieldtypeType?.value, 0, { type: mapping?.type }, ignoreDefaults)]
          }
        }
        break
      case 'enum':
        if (fieldtypeType?.value && Array.isArray(fieldtypeType.value) && fieldtypeType.value.length) {
          mapping.info.picklist = fieldtypeType.value.map(v => v.value.replace(/'/g, ''))
          if (defaultProps && (mapping.info.picklist as unknown[]).includes(defaultProps)) {
            mapping.value = defaultProps as string;
            mapping.bindingType = 'value'
          }
        }
        break
      case 'hidden':
        mapping.value = value as any;
        break
      default:
        if (defaultProps && fieldtypeType && fieldtypeType.raw !== 'multiselect' && !value) {
          // if theres a defaultProp of a primitive type,
          // set the mapping.value to that
          mapping.value = defaultProps as any;
          mapping.bindingType = 'value'
        } else if (value && !mapping.value) {
          mapping.value = value as any;
        }
        break
    }
  }
  mapping.dataSource = null
  //  mapping = Mapping.create(mapping)
  return mapping
}

function createObjectMappingChildren (defaultProps, mapping: Partial<IMapping>) {
  return Object.keys(defaultProps).map(p => {
    const childVal: MappingValue = { name: undefined, value: undefined }
    const propVal = defaultProps[p]
    if (propVal) {
      // complex nested defaultProps won't have types, below calculates it
      childVal.name = isArray(propVal) ? 'list' : typeof propVal
      childVal.value = propVal
    }
    return forType(
      { ...childVal, defaultValue: { value: defaultProps[p] } },
      p,
      { type: mapping?.type }
    )
  })
}

export default Mapping
export interface IMapping extends Instance<typeof Mapping> {}
export interface IMappingSnapshot extends SnapshotIn<typeof Mapping> {}

/* 'any' type is added to allow for incremental types expansion. Should add typization to Component model in the future. */
const ComponentRefType: any = t.union(
  t.late(() => Component),
  t.late(() => ComponentItem)
)

const CompRef =  t.model({
  id: t.identifier,
  comp: safeReference(ComponentRefType)
});

type IMappingItemType = IMapping & Instance<typeof CompRef>
export interface IMappingItem extends IMappingItemType {}

type IMappingItemSnapshotType = IMappingSnapshot & SnapshotIn<typeof CompRef>
export interface IMappingItemSnapshot extends IMappingItemSnapshotType {}

export const MappingItem = t.compose(
  'MappingItem',
  Mapping,
  t.model({
    id: t.identifier,
    comp: safeReference(ComponentRefType)
  }).views(self => ({
    get ssrProps() {
      return ssrProps(self.comp?.id, getEnv(self)?.props)
    }
  }))
)
